import {
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Info } from 'lucide-react';

// A small "ⓘ" button that reveals secondary details (credits, provenance) on
// demand. Opens on hover for mouse users and on click / tap / Enter for touch
// and keyboard users; Escape and an outside click close it. The content is
// portalled, so a scrolling or `overflow-hidden` parent never clips it.
//
// Hover is a convenience, not the only way in: the content may hold a link,
// which a pure hover tooltip could never make reachable.

// Grace period so the pointer can travel from the icon into the panel.
const HOVER_CLOSE_MS = 150;

interface InfoPopoverProps {
  /** Accessible name for the icon button, e.g. "Image details and credit". */
  label: string;
  children: ReactNode;
}

export function InfoPopover({ label, children }: InfoPopoverProps) {
  const [open, setOpen] = useState(false);
  // A click pins the panel open, so leaving with the mouse no longer closes it.
  const pinned = useRef(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();

  const cancelClose = () => clearTimeout(closeTimer.current);
  const openNow = () => {
    cancelClose();
    setOpen(true);
  };
  const closeSoon = () => {
    cancelClose();
    if (pinned.current) return;
    closeTimer.current = setTimeout(() => setOpen(false), HOVER_CLOSE_MS);
  };
  const onOpenChange = (next: boolean) => {
    if (!next) pinned.current = false;
    setOpen(next);
  };
  // Hovering opened the panel just before this click; without this the click
  // would toggle it straight back shut. Pin it instead.
  const onTriggerClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (open && !pinned.current) {
      e.preventDefault();
      cancelClose();
      pinned.current = true;
    }
  };

  useEffect(() => cancelClose, []);

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger
        aria-label={label}
        onClick={onTriggerClick}
        onPointerEnter={e => e.pointerType === 'mouse' && openNow()}
        onPointerLeave={e => e.pointerType === 'mouse' && closeSoon()}
        className='tap-target inline-flex size-6 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors duration-200 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      >
        <Info className='size-3.5' aria-hidden='true' />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          aria-label={label}
          side='top'
          align='center'
          sideOffset={6}
          collisionPadding={16}
          onPointerEnter={openNow}
          onPointerLeave={closeSoon}
          className='z-40 w-72 max-w-[calc(100vw-2rem)] rounded-md border border-border bg-popover p-3 text-left text-xs leading-relaxed text-popover-foreground shadow-elevation-2 motion-safe:duration-200 motion-safe:animate-in motion-safe:fade-in-0'
        >
          {children}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
